import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Bell,
  Check,
  ChevronRight,
  ClipboardList,
  Clock,
  RotateCcw,
  Send,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { MyForm, MyFormList, Submission, SubmissionList } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { cn } from '@up2d8/ui';
import { labelOf } from '../forms/field-input';
import { describe as describeValue } from '../forms/submission-row';
import { canWithdraw, displayStatus, type DisplayStatus } from '../forms/submission-display';
import { useOffline } from '../../offline/use-offline';
import { useSession } from '../../shared/session';
import { useTenantTheme } from '../../shared/use-tenant-theme';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { DeskShell } from './desk/desk-shell';
import { FloorField } from './floor/floor-field';
import {
  Crumbs,
  EmptyCard,
  FloorBadge,
  OfflineStrip,
  SkeletonCards,
  useDayTime,
  useFloorToast,
  type BadgeTone,
} from './floor/floor-kit';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';

/**
 * Formulieren voor de medewerker (Stap 37), in de vloermodus (VLR-3,
 * `docs/design/UP2D8-formulieren-replica-spec.md`).
 *
 * Eén scherm met twee secties: wat je kunt invullen, en wat je ingediend hebt. Geen tabbladen: een
 * verlofaanvraag indienen en kijken of de vorige goedgekeurd is, zijn dezelfde handeling met een
 * week ertussen. Op het bureau staan ze naast elkaar.
 *
 * Een formulier opent **in** dit scherm en niet op een eigen pagina: op een telefoon is
 * terugnavigeren na een mislukte verzending de snelste manier om ingevulde velden kwijt te raken.
 * `?formulier=` opent er meteen één; zo linkt "Mijn taken" ernaartoe.
 */

/** Ouder dan dertig dagen: ingeklapt, net als een ingetrokken aanvraag (spec §2). */
const OLD_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Wanneer deze medewerker hier vorige keer keek, **op dit toestel**.
 *
 * "Beslist sinds je laatste bezoek" vraagt een tijdstip, en de spec laat de keuze: per gebruiker op
 * de server, of per toestel. Per toestel, want het is een markering en geen bewijs — wie op een
 * tweede toestel nog eens dezelfde blauwe rand ziet, verliest niets. Staat in `deviations.md`.
 */
function useLastVisit(personId: string | null | undefined): string | null {
  const key = `up2d8.forms.lastVisit.${personId ?? 'anon'}`;
  const [previous] = useState<string | null>(() => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, new Date().toISOString());
    } catch {
      // Privémodus: dan is er gewoon geen markering.
    }
  }, [key]);
  return previous;
}

function decidedSince(submission: Submission, lastVisit: string | null): boolean {
  return (
    lastVisit !== null &&
    submission.decidedAt !== null &&
    (submission.status === 'approved' || submission.status === 'rejected') &&
    submission.decidedAt > lastVisit
  );
}

export function AppFormsPage() {
  const { t } = useTranslation();
  const session = useSession();
  const tabs = useAppTabs();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { formulier?: string };
  const { online } = useOffline();
  const { show, element: toast } = useFloorToast();
  const [openFormId, setOpenFormId] = useState<string | null>(search.formulier ?? null);
  const lastVisit = useLastVisit(session.profile?.personId);

  useTenantTheme();

  const forms = useQuery({
    queryKey: ['me', 'forms'],
    queryFn: async (): Promise<MyForm[]> => {
      const { data, error } = await session.api.GET('/api/v1/me/forms', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as MyFormList).items;
    },
  });

  const submissions = useQuery({
    queryKey: ['me', 'forms', 'submissions'],
    queryFn: async (): Promise<Submission[]> => {
      const { data, error } = await session.api.GET('/api/v1/me/forms/submissions', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as SubmissionList).items;
    },
  });

  const open = (forms.data ?? []).find((form) => form.formId === openFormId) ?? null;
  const all = useMemo(() => {
    const items = [...(submissions.data ?? [])].sort((a, b) =>
      b.submittedAt.localeCompare(a.submittedAt),
    );
    // Wat beslist is sinds je laatste bezoek, staat bovenaan.
    return [
      ...items.filter((item) => decidedSince(item, lastVisit)),
      ...items.filter((item) => !decidedSince(item, lastVisit)),
    ];
  }, [submissions.data, lastVisit]);
  const fresh = all.filter((item) => decidedSince(item, lastVisit)).length;

  const close = () => {
    setOpenFormId(null);
    if (search.formulier) void navigate({ to: '/app/formulieren', search: {}, replace: true });
  };

  const shell = {
    tabs,
    activeKey: 'home',
    deskActiveKey: 'checklists',
    renderLink: renderAppTabLink,
    messages: <MessagesIcon />,
    bell: <NotificationBell />,
  };

  if (open) {
    return (
      <FillForm
        key={open.formId}
        form={open}
        shell={shell}
        online={online}
        previous={all.filter((item) => item.formId === open.formId)}
        lastVisit={lastVisit}
        onClose={close}
        onSubmitted={(text) => {
          close();
          show({ tone: 'ok', text });
        }}
        onFailed={(text) => show({ tone: 'err', text })}
        toast={toast}
      />
    );
  }

  const strip =
    fresh > 0 ? (
      <div className="cf-newstrip" role="status" data-testid="forms-decided-strip">
        <Bell aria-hidden width={20} height={20} />
        {t('appForms.decidedStrip', { count: fresh })}
      </div>
    ) : null;

  return (
    <DeskShell
      {...shell}
      subBar={{
        title: t('appForms.title'),
        onBack: () => void navigate({ to: '/app' }),
        backLabel: t('floor.back'),
      }}
    >
      <div className="fm cf-grid" data-testid="forms-page">
        <div className="cf-col">
          <div className="cf-desk-only flex-col">
            <Crumbs
              items={[
                { label: t('myTasks.title'), to: '/app/taken' },
                { label: t('appForms.title') },
              ]}
            />
            <h1 className="cf-title">{t('appForms.title')}</h1>
            <div className="cf-sub">{t('appForms.sub')}</div>
          </div>
          {strip ? <div className="cf-phone-only">{strip}</div> : null}

          <div className="fm-section">
            <h2 className="fm-h cf-phone-only">
              {t('appForms.fill')}{' '}
              <span className="fm-h-count">
                {forms.isPending ? '…' : (forms.data?.length ?? 0)}
              </span>
            </h2>
            {forms.isPending ? (
              <SkeletonCards count={2} />
            ) : forms.isError ? (
              <EmptyCard
                tone="danger"
                icon={<TriangleAlert aria-hidden width={32} height={32} />}
                title={t('errors.generic')}
                text={t('appForms.emptyHint')}
              />
            ) : (forms.data ?? []).length === 0 ? (
              <EmptyCard
                tone="neutral"
                icon={<ClipboardList aria-hidden width={32} height={32} />}
                title={t('appForms.emptyTitle')}
                text={t('appForms.emptyHint')}
              />
            ) : (
              <div className="cf-cards-2">
                {(forms.data ?? []).map((form) => (
                  <button
                    key={form.formId}
                    type="button"
                    className="fm-card cf-listcard"
                    onClick={() => setOpenFormId(form.formId)}
                    data-testid="form-card"
                  >
                    <div className="cf-roundicon primary">
                      <ClipboardList aria-hidden width={24} height={24} />
                    </div>
                    <div className="cf-listcard-info">
                      <div className="fm-title">{form.title}</div>
                      {form.description ? <div className="cf-desc">{form.description}</div> : null}
                      {form.openOfMine > 0 ? (
                        <div className="cf-badges">
                          <FloorBadge
                            tone="attention"
                            icon={<Clock aria-hidden width={14} height={14} />}
                          >
                            {t('appForms.openOfMine', { count: form.openOfMine })}
                          </FloorBadge>
                        </div>
                      ) : null}
                    </div>
                    <ChevronRight aria-hidden width={22} height={22} className="fm-chev" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="cf-aside" data-testid="my-submissions">
          {strip ? <div className="cf-desk-only flex-col">{strip}</div> : null}
          <h2 className="fm-h">
            {t('appForms.mine')}{' '}
            <span className="fm-h-count">{submissions.isPending ? '…' : all.length}</span>
          </h2>
          {submissions.isPending ? (
            <SkeletonCards count={1} />
          ) : all.length === 0 ? (
            <div className="fm-card">
              <div className="fm-meta">{t('appForms.noneYet')}</div>
            </div>
          ) : (
            all.map((submission) => (
              <SubmissionCard
                key={submission.id}
                submission={submission}
                fresh={decidedSince(submission, lastVisit)}
                onError={(text) => show({ tone: 'err', text })}
                onWithdrawn={(text) => show({ tone: 'ok', text })}
              />
            ))
          )}
        </aside>
        {toast}
      </div>
    </DeskShell>
  );
}

type ShellProps = Pick<
  Parameters<typeof DeskShell>[0],
  'tabs' | 'activeKey' | 'deskActiveKey' | 'renderLink' | 'messages' | 'bell'
>;

function FillForm({
  form,
  shell,
  online,
  previous,
  lastVisit,
  onClose,
  onSubmitted,
  onFailed,
  toast,
}: {
  form: MyForm;
  shell: ShellProps;
  online: boolean;
  previous: Submission[];
  lastVisit: string | null;
  onClose: () => void;
  onSubmitted: (text: string) => void;
  onFailed: (text: string) => void;
  toast: ReactNode;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();

  const [values, setValues] = useState<Record<string, unknown>>({ ...form.prefill });
  const [errors, setErrors] = useState<Record<string, string>>({});

  /**
   * Eén regel vooraf, op het scherm: een einddatum vóór de begindatum. De server weigert die ook,
   * maar dan pas na Indienen; dit zegt het onder het veld zodra het zo is (spec F2).
   */
  const localErrors = useMemo(() => {
    const result: Record<string, string> = {};
    for (const field of form.fields) {
      if (field.type !== 'daterange') continue;
      const range = (values[field.key] ?? {}) as { from?: string; to?: string };
      if (range.from && range.to && range.to < range.from) {
        result[field.key] = t('appForms.endBeforeStart');
      }
    }
    return result;
  }, [form.fields, values, t]);

  const submit = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/api/v1/me/forms/{formId}/submissions', {
        params: { path: { formId: form.formId } },
        body: { data: values },
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      // Ook "Te doen": een ingevuld formulier staat daar niet meer open.
      void queryClient.invalidateQueries({ queryKey: ['open-items'] });
      await queryClient.invalidateQueries({ queryKey: ['me', 'forms'] });
      onSubmitted(form.needsHandling ? t('appForms.sentForHandling') : t('appForms.sent'));
    },
    onError: (error) => {
      const fieldErrors = (error as { errors?: { path: string; message: string }[] } | undefined)
        ?.errors;
      if (fieldErrors?.length) {
        setErrors(Object.fromEntries(fieldErrors.map((item) => [item.path, item.message])));
        onFailed(t('appForms.fixFields'));
        return;
      }
      setErrors({});
      // Het formulier blijft ingevuld staan: de fout is van ons, niet van wat er ingevuld werd.
      onFailed(problemDetail(error) ?? t('appForms.failed'));
    },
  });

  const blocked = !online || Object.keys(localErrors).length > 0;
  const send = () => {
    if (blocked) return;
    submit.mutate();
  };

  const submitButton = (
    <button
      type="button"
      className={cn('fm-btn', blocked && 'is-disabled')}
      disabled={blocked || submit.isPending}
      onClick={send}
      data-testid="form-submit"
    >
      <Send aria-hidden width={20} height={20} />
      {t('appForms.submit')}
    </button>
  );
  const cancelButton = (
    <button type="button" className="fm-btn outline" onClick={onClose} data-testid="form-cancel">
      {t('common.cancel')}
    </button>
  );
  const offlineHint = !online ? (
    <div className="fm-hint" style={{ textAlign: 'center' }}>
      {t('appForms.offlineSubmit')}
    </div>
  ) : null;

  return (
    <DeskShell
      {...shell}
      subBar={{ title: t('appForms.form'), onBack: onClose, backLabel: t('floor.back') }}
      mobileFooter={
        <div className="fm-footer">
          {offlineHint}
          <div className="fm-footer-row">
            {cancelButton}
            {submitButton}
          </div>
        </div>
      }
    >
      <div className="fm cf-grid" data-testid="form-fill">
        {!online ? (
          <div className="w-full" style={{ gridColumn: '1 / -1' }}>
            <OfflineStrip>{t('appForms.offline')}</OfflineStrip>
          </div>
        ) : null}
        <div className="cf-col">
          <Crumbs
            items={[
              { label: t('myTasks.title'), to: '/app/taken' },
              { label: t('appForms.title'), onClick: onClose },
              { label: form.title },
            ]}
          />
          <div className="ff-sheet">
            <div className="cl-head-top">
              <div className="cf-roundicon primary">
                <ClipboardList aria-hidden width={24} height={24} />
              </div>
              <div className="cf-listcard-info" style={{ gap: 2 }}>
                <h1 className="cf-title cf-desk-title">{form.title}</h1>
                <div className="fm-big cf-phone-only">{form.title}</div>
                {form.description ? <div className="cf-desc">{form.description}</div> : null}
              </div>
            </div>
            <div className="ff-group">
              {form.fields.map((field) => (
                <FloorField
                  key={field.key}
                  field={field}
                  value={values[field.key]}
                  error={localErrors[field.key] ?? errors[field.key]}
                  offline={!online}
                  onChange={(value) => {
                    setValues((current) => ({ ...current, [field.key]: value }));
                    setErrors((current) => {
                      if (!(field.key in current)) return current;
                      const next = { ...current };
                      delete next[field.key];
                      return next;
                    });
                  }}
                />
              ))}
            </div>
            <div className="ff-desk-actions">
              {offlineHint}
              <div className="ff-actions">
                {submitButton}
                {cancelButton}
              </div>
            </div>
          </div>
        </div>

        {previous.length > 0 ? (
          <aside className="cf-aside cf-desk-only">
            <h2 className="fm-h">{t('appForms.previous')}</h2>
            {previous.map((submission) => (
              <SubmissionCard
                key={submission.id}
                submission={submission}
                fresh={decidedSince(submission, lastVisit)}
                onError={onFailed}
                onWithdrawn={() => undefined}
              />
            ))}
          </aside>
        ) : null}
        {toast}
      </div>
    </DeskShell>
  );
}

const STATUS: Record<DisplayStatus, { tone: BadgeTone; icon: typeof Check }> = {
  submitted: { tone: 'attention', icon: Clock },
  // Alleen verzamelen: ontvangen, er komt geen beslissing (testrapport 28 okt., #8).
  received: { tone: 'neutral', icon: Check },
  approved: { tone: 'ok', icon: Check },
  rejected: { tone: 'danger', icon: X },
  withdrawn: { tone: 'neutral', icon: RotateCcw },
};

/**
 * Eén aanvraag. Ingetrokken en ouder dan dertig dagen: één ingeklapte rij van 64 hoog, die
 * openklapt als je erop tikt (spec §2).
 */
function SubmissionCard({
  submission,
  fresh,
  onError,
  onWithdrawn,
}: {
  submission: Submission;
  fresh: boolean;
  onError: (text: string) => void;
  onWithdrawn: (text: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const queryClient = useQueryClient();
  const dayTime = useDayTime();
  const collapsible =
    !fresh &&
    (submission.status === 'withdrawn' ||
      Date.now() - new Date(submission.submittedAt).getTime() > OLD_MS);
  const [expanded, setExpanded] = useState(false);

  const withdraw = useMutation({
    mutationFn: async () => {
      const { error } = await session.api.POST(
        '/api/v1/me/forms/submissions/{submissionId}/withdraw',
        { params: { path: { submissionId: submission.id } } },
      );
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me', 'forms'] });
      onWithdrawn(t('forms.withdrawn'));
    },
    onError: () => onError(t('errors.generic')),
  });

  const shown = displayStatus(submission);
  const status = STATUS[shown];
  const StatusIcon = status.icon;
  const badge = (
    <FloorBadge tone={status.tone} icon={<StatusIcon aria-hidden width={14} height={14} />}>
      {t(`forms.status.${shown}`)}
    </FloorBadge>
  );
  const submitted = t('appForms.submittedOn', { when: dayTime(submission.submittedAt) });

  if (collapsible && !expanded) {
    return (
      <button
        type="button"
        className="fm-card rq-compact"
        aria-expanded={false}
        onClick={() => setExpanded(true)}
        data-testid="my-submission"
      >
        <div className="cf-listcard-info" style={{ gap: 2 }}>
          <div className="rq-title">{submission.formTitle}</div>
          <div className="fm-meta">{submitted}</div>
        </div>
        {badge}
        <ChevronRight aria-hidden width={22} height={22} className="fm-chev" />
      </button>
    );
  }

  const decidedBy = [
    submission.decidedByName,
    submission.decidedAt
      ? new Intl.DateTimeFormat(i18n.language, { day: '2-digit', month: '2-digit' }).format(
          new Date(submission.decidedAt),
        )
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className={cn('fm-card rq', fresh && 'new')} data-testid="my-submission">
      {fresh ? <span className="rq-newdot">{t('appForms.decidedSince')}</span> : null}
      <div className="rq-top">
        <div className="cf-listcard-info">
          <div className="rq-title">{submission.formTitle}</div>
          <div className="fm-meta">{submitted}</div>
        </div>
        {badge}
      </div>
      {submission.fields.length > 0 ? (
        <dl className="rq-dl">
          {submission.fields.map((field) => (
            <div key={field.key} className="contents">
              <dt>{labelOf(field.label, i18n.language, field.key)}</dt>
              <dd>{describeValue(field, submission.data[field.key], i18n.language)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {submission.decisionNote ? (
        <div className="rq-note">
          {decidedBy ? <span className="rq-note-by">{decidedBy}</span> : null}
          {submission.decisionNote}
        </div>
      ) : null}
      {canWithdraw(submission) ? (
        <button
          type="button"
          className="fm-btn outline block"
          disabled={withdraw.isPending}
          onClick={() => withdraw.mutate()}
          data-testid="withdraw-submission"
        >
          <RotateCcw aria-hidden width={18} height={18} />
          {t('forms.withdraw')}
        </button>
      ) : null}
    </article>
  );
}
